import { inject, Injectable, Signal } from '@angular/core';
import { OptimizelyDecision } from '@optimizely/optimizely-sdk';
import { endsWith } from 'lodash';
import { map, Observable, of, switchMap } from 'rxjs';
import { IIqTrendlinesFfStateService } from '../../models';
import { IqTrendlinesFeatureFlagsDbService } from './iq-trendlines-ff-db.service';
import { IqTrendlinesOptimizelyService } from './iq-trendlines-optimizely.service';

@Injectable({
    providedIn: 'root',
})
export class IqTrendlinesFfStateService implements IIqTrendlinesFfStateService {
    private _optimizelyService: IqTrendlinesOptimizelyService = inject(
        IqTrendlinesOptimizelyService,
    );
    private _db: IqTrendlinesFeatureFlagsDbService = inject(
        IqTrendlinesFeatureFlagsDbService,
    );

    isFlagEnabledSig(flagName: string): Signal<boolean> {
        return this._optimizelyService.isFlagEnabled(flagName);
    }

    hasVariationSig(flagName: string, variation: string): Signal<boolean> {
        return this._optimizelyService.hasVariation(flagName, variation);
    }

    getFeatureFlagSig(flagName: string): Signal<OptimizelyDecision | null> {
        return this._optimizelyService.getFeatureFlag(flagName);
    }

    isExperimentFlagEnrolled(flagName: string): Observable<boolean> {
        const flag = this._optimizelyService.getFeatureFlag(flagName)();
        if (!flag) return of(false);
        return this._db.hasFlag(flag.flagKey).pipe(
            switchMap((isFlagStored: boolean) => {
                if (!isFlagStored) return of(false);
                return this._db
                    .getFlag(flag.flagKey)
                    .pipe(
                        map(
                            (storedFlag) =>
                                storedFlag?.toJSON()?.variationKey ===
                                flag.variationKey,
                        ),
                    );
            }),
        );
    }

    enrollExperimentFlag(flagName: string): Observable<void> {
        this._optimizelyService.enrollExperimentFlag(flagName);
        const flag = this._optimizelyService.getFeatureFlag(flagName)();
        if (!flag) return of();
        const isExperiment = endsWith(flag.ruleKey, 'experiment');
        if (!isExperiment) {
            return this._db.removeFlag(flagName);
        }
        const { ruleKey, variationKey } = flag;
        return this._db.upsertFlag(flagName, ruleKey, variationKey);
    }
}
